<template>
  <div id="html-editor"></div>
</template>
<script lang="ts" setup>
  import xss from "xss";
  import cssfilter from "cssfilter";
  import * as diff from "diff";
  import { useI18n } from "vue-i18n";
  import { useMjmlStore } from "@/stores/mjmlStore";
  import getXssWhiteList from "@/utils/getXssWhiteList";
  import { getCopiedElement } from "@topol/utils";
  import { unescapeConditionalHtmlComments } from "@topol/utils";
  import { ref, watch, onMounted, onBeforeUnmount, computed } from "vue";
  import { useMergeTagsStore } from "@/stores/mergeTags";

  const mergeTagsStore = useMergeTagsStore();

  const props = defineProps(["content", "lang", "theme"]);

  const emits = defineEmits([
    "change-content",
    "show-back",
    "hide-back",
    "fix-content",
    "blur",
  ]);

  const mjmlStore = useMjmlStore();
  const { t } = useI18n();

  const editor = ref<any>();
  const blockIndexes = ref();
  const beforeContent = ref("");
  const markers = ref<number[]>([]);
  const openingTags = ref<any[]>([]);
  const openingTagIndex = ref<number>(0);
  const isValid = ref(true);
  const isStyle = ref(false);
  const unclosedIndex = ref(0);
  const settingNewContent = ref(false);

  const newContent = computed(() => {
    return props.content;
  });

  const newCssWhiteList = cssfilter.whiteList;
  for (const key in newCssWhiteList) {
    newCssWhiteList[key] = true;
  }

  const newWhiteList = getXssWhiteList();

  function allowDataAttributes(
    _tag: string,
    name: string,
    value: string
  ): string | void {
    if (name.startsWith("data-")) {
      // @ts-expect-error
      return `${name}="${xss.escapeAttrValue(value)}"`;
    }
  }

  function fixHTML() {
    const sanitizedHTML = highlightInvalidHTML(
      editor.value.getValue(),
      editor.value
    );
    if (!isValid.value) {
      beforeContent.value = sanitizedHTML;
      emits("fix-content", sanitizedHTML);
      emits("show-back");
    }
  }

  function clearAllMarkers(aceEditor: any) {
    markers.value.forEach((markerId) => {
      aceEditor.getSession().removeMarker(markerId);
    });
    markers.value = [];
  }

  function closeTag(tag: string) {
    let isClosing = false;
    for (let i = openingTags.value.length - 1; i >= 0; i--) {
      if (openingTags.value[i].tag === tag && !isClosing) {
        isClosing = true;
        openingTags.value.splice(i, 1);
        break;
      }
    }
    return isClosing;
  }

  function onTagRemoveUnusedClosingTags(
    tag: string,
    html: string,
    options: any
  ) {
    if (tag === "style") {
      isStyle.value = !options.isClosing;
    }
    if (options.isClosing && !closeTag(tag)) {
      return "";
    }

    if (!options.isClosing && !html.endsWith("/>") && tag !== "img") {
      openingTags.value.unshift({ tag: tag, index: openingTagIndex.value });
      openingTagIndex.value++;
    }
    //@ts-expect-error
    return xss.onTag(tag, html, options);
  }

  function mergetagRegexTest(str: string) {
    const placeReg = /^MERGE_TAG_\d+_PLACEHOLDER/;
    return placeReg.test(str);
  }

  function getMergetagsRegex() {
    const syntax = mergeTagsStore.smartMergeTags?.syntax || {
      start: "*|",
      end: "|*",
    };

    const merge_tags_syntax_start_escaped =
      "\\" + syntax.start.split("").join("\\");
    const merge_tags_syntax_end_escaped =
      "\\" + syntax.end.split("").join("\\");

    return new RegExp(
      `${merge_tags_syntax_start_escaped}[\\s\\S]*?${merge_tags_syntax_end_escaped}`,
      "g"
    );
  }

  function customSafeAttrValue(tag: string, name: string, value: string) {
    if (name === "style") {
      if (
        value.replaceAll(" ", "") ===
        cssfilter(value, {
          whiteList: newCssWhiteList,
        }).replaceAll(" ", "")
      ) {
        return value;
      } else
        return cssfilter(value, {
          whiteList: newCssWhiteList,
        });
    }

    if ((name === "href" || name === "src") && mergetagRegexTest(value)) {
      return value;
    }

    //@ts-expect-error
    return xss.safeAttrValue(tag, name, value);
  }

  function noEscapeStyle(html: string) {
    if (isStyle.value) {
      return html;
    }
    //@ts-expect-error
    return xss.escapeHtml(html);
  }

  function removeUnclosedTags(tag: string, html: string, options: any) {
    if (!options.isClosing && !html.endsWith("/>")) {
      if (
        openingTags.value.find(
          (tagObject) =>
            tagObject.index === unclosedIndex.value && tagObject.tag === tag
        )
      ) {
        unclosedIndex.value++;
        if (tag === "style") {
          isStyle.value = false;
        }
        return html.replace(">", " />");
      }
      unclosedIndex.value++;
    }
    if (tag === "style") {
      isStyle.value = !options.isClosing;
    }
    // @ts-expect-error
    return xss.onTag(tag, html, options);
  }

  function getSanitizedHTMLWithoutUnusedClosingTags(html: string) {
    openingTags.value = [];
    openingTagIndex.value = 0;
    const options = {
      allowCommentTag: true,
      whiteList: newWhiteList,
      onTag: onTagRemoveUnusedClosingTags,
      onIgnoreTagAttr: allowDataAttributes,
      safeAttrValue: customSafeAttrValue,
      escapeHtml: noEscapeStyle,
    };
    return xss(html, options);
  }

  function getSanitizedHTMLWithoutUnclousedTags(html: string) {
    unclosedIndex.value = 0;
    const options = {
      allowCommentTag: true,
      whiteList: newWhiteList,
      onTag: removeUnclosedTags,
      onIgnoreTagAttr: allowDataAttributes,
      safeAttrValue: customSafeAttrValue,
      escapeHtml: noEscapeStyle,
    };
    return xss(html, options);
  }

  function highlightInvalidHTML(html: string, aceEditor: any) {
    isValid.value = true;
    clearAllMarkers(aceEditor);

    const replacementsMT: {
      placeholder: string;
      original: string;
    }[] = [];

    const sanitizedMT = html.replace(getMergetagsRegex(), (match) => {
      const placeholder = `MERGE_TAG_${replacementsMT.length}_PLACEHOLDER`;
      replacementsMT.push({ placeholder: placeholder, original: match });
      return placeholder;
    });

    const sanitizedHTML = getSanitizedHTMLWithoutUnusedClosingTags(sanitizedMT);
    let sanitizedHTMLWithComments =
      unescapeConditionalHtmlComments(sanitizedHTML);
    if (openingTags.value.length > 0) {
      const resanitizedHTML =
        getSanitizedHTMLWithoutUnclousedTags(sanitizedHTML);
      sanitizedHTMLWithComments =
        unescapeConditionalHtmlComments(resanitizedHTML);
    }

    if (!sanitizedHTMLWithComments) {
      return html;
    }

    replacementsMT.forEach((replacement) => {
      sanitizedHTMLWithComments = sanitizedHTMLWithComments.replace(
        replacement.placeholder,
        replacement.original
      );
    });

    if (html !== sanitizedHTMLWithComments) {
      isValid.value = false;
      const annotations: any[] = [];
      const sanitizedLines = sanitizedHTMLWithComments.split("\n");
      const differences = diff
        .diffChars(html, sanitizedHTMLWithComments)
        .filter((difference) => !difference.added);
      let difIndex = 0;
      let lineIndex = 0;
      let sanitizeLines = 0;
      let removedLines = 0;
      let annotationLineAdds = 0;
      differences.forEach((difference) => {
        if (!difference.count) return;
        const startLine = lineIndex;
        const startDifIndex = difIndex;
        const startSanitizeLines = sanitizeLines;
        const match = difference.value.match(/^\t*/);
        difIndex += difference.count;
        if (difference.value.includes("\n")) {
          const splitByLine = difference.value.split("\n");
          lineIndex += splitByLine.length - 1;
          difIndex = splitByLine[splitByLine.length - 1].length;
          if (difference.removed) {
            removedLines += splitByLine.length - 1;
          }
          sanitizeLines = lineIndex - removedLines;
        }
        const onlyNewLine = match && match[0] ? "\n" + match[0] : "\n";
        if (difference.removed && difference.value !== onlyNewLine) {
          const marker = aceEditor.getSession().addMarker(
            // @ts-expect-error
            new ace.Range(startLine, startDifIndex, lineIndex, difIndex),
            "myCustomMouseOverHighlight",
            "text"
          );
          markers.value.push(marker);
          if (startSanitizeLines !== annotationLineAdds || startLine === 0) {
            annotationLineAdds = startSanitizeLines;
            const sanizeText = match
              ? sanitizedLines[startSanitizeLines].replace(match[0], "")
              : sanitizedLines[startSanitizeLines];
            annotations.push({
              row: startLine,
              column: 0,
              text: t("errors.invalid_HTML_row") + "\n" + sanizeText,
              type: "error",
            });
          }
        }
      });

      setTimeout(() => {
        aceEditor.getSession().setAnnotations(annotations);
      }, 10);
    } else {
      aceEditor.getSession().clearAnnotations();
    }

    return sanitizedHTMLWithComments;
  }

  watch(newContent, (value) => {
    if (beforeContent.value !== value) {
      settingNewContent.value = true;
      editor.value.setValue(value, 1);
    }
  });

  defineExpose({
    isValid,
    fixHTML,
  });

  onMounted(() => {
    blockIndexes.value = JSON.parse(JSON.stringify(mjmlStore.selectedBlock));
    const lang = props.lang || "html";

    //@ts-expect-error
    // eslint-disable-next-line no-undef
    editor.value = ace.edit("html-editor");
    editor.value.setValue(props.content, 1);

    // mode-xxx.js or theme-xxx.jsがある場合のみ有効
    editor.value.getSession().setMode(`ace/mode/${lang}`);

    //Remove unreasonable error that HTML doesnt start with DOCTYPE
    //This is unfortunately effecting all of the syntax checking
    //But there is no point in using the error checking when the DOCTYPE error is still in place
    editor.value.getSession().setUseWorker(false);

    editor.value.$blockScrolling = Infinity;
    highlightInvalidHTML(editor.value.getValue(), editor.value);

    editor.value.on("change", () => {
      highlightInvalidHTML(editor.value.getValue(), editor.value);
      beforeContent.value = editor.value.getValue();
      if (settingNewContent.value) {
        settingNewContent.value = false;
        return;
      }
      emits("change-content", editor.value.getValue());
      emits("hide-back");
    });
    editor.value.on("blur", () => {
      emits("blur");
    });
    editor.value.onPaste = function (text: string, event?: ClipboardEvent) {
      if (getCopiedElement(text)) {
        return;
      }
      const e = { text: text, event: event };
      editor.value.commands.exec("paste", editor.value, e);
    };
  });

  onBeforeUnmount(() => {
    if (
      editor.value &&
      JSON.stringify(blockIndexes.value) !==
        JSON.stringify(mjmlStore.selectedBlock)
    ) {
      fixHTML();
    }
  });
</script>

<style lang="scss">
  .myCustomMouseOverHighlight {
    border-bottom: 1px solid red;
    position: absolute;
    pointer-events: auto;
    z-index: 3;
  }
  .ace-tm .ace_gutter {
    background: var(--c-g600);
    color: var(--c-g100);
  }
  .ace-tm .ace_print-margin {
    background-color: var(--c-g500);
  }
  .ace-tm {
    background: var(--c-g800);
    color: var(--c-g10);
  }
  .ace-tm .ace_cursor {
    color: var(--c-g50);
  }
  .ace-tm .ace_gutter-active-line {
    background-color: var(--c-g400);
  }
  .ace-tm .ace_marker-layer .ace_active-line {
    background: var(--c-g600);
  }
  .ace-tm .ace_marker-layer .ace_selection {
    background: var(--c-g300);
  }
  .ace_punctuation,
  .ace_punctuation.ace_tag,
  .ace-tm .ace_keyword.ace_operator {
    color: var(--c-g100) !important;
  }
  .ace-tm .ace_meta.ace_tag,
  .ace-tm .ace_storage {
    color: var(--c-content-1);
  }
  .ace-tm .ace_indent-guide {
    box-sizing: border-box;
    background: none;
    box-shadow: 1px 0px 0px var(--c-g600);
  }
  .ace-tm .ace_string {
    color: var(--c-accent-3);
  }
  .ace-tm .ace_entity.ace_other,
  .ace-tm .ace_entity.ace_other.ace_attribute-name,
  .ace-tm .ace_entity.ace_name.ace_function {
    color: #b4a0ff;
  }
  .ace-tm .ace_storage.ace_type,
  .ace-tm .ace_support.ace_class,
  .ace-tm .ace_support.ace_type {
    color: var(--c-content-1);
  }
  .ace-tm .ace_constant.ace_character,
  .ace-tm .ace_constant.ace_language,
  .ace-tm .ace_constant.ace_numeric,
  .ace-tm .ace_constant.ace_other {
    color: var(--c-destructive-2);
  }
  .ace-tm .ace_variable.ace_parameter {
    font-style: italic;
    color: var(--c-content-2);
  }
  .ace-tm .ace_keyword {
    color: var(--c-accent-1);
  }

  .light-theme {
    .ace-tm .ace_meta,
    .ace-tm .ace_storage {
      color: var(--c-structure-2);
    }
    .ace-tm .ace_entity.ace_other,
    .ace-tm .ace_entity.ace_other.ace_attribute-name,
    .ace-tm .ace_entity.ace_name.ace_function {
      color: var(--c-content-2);
    }
    .ace-tm .ace_variable.ace_parameter {
      font-style: italic;
      color: var(--c-structure-1);
    }
    .ace-tm .ace_marker-layer .ace_selection {
      background: var(--c-g400);
    }
  }
</style>
